import { t } from "../../i18n";
import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, GripVertical } from "lucide-react";
import type { Model } from "@lumen/shared";
import { api } from "../../api";
import { ModelSearchPicker } from "../composer/ModelSearchPicker";

export type CustomModelScope = "platform" | "organization" | "personal";
export type CustomModelType = "routing" | "fallback";
export type CustomModelOption = {
  id: string;
  name: string;
  providerName: string;
  scope: CustomModelScope;
};
export type CustomModelRecord = {
  id: string;
  name: string;
  description: string | null;
  type: CustomModelType;
  scope: CustomModelScope;
  organizationId: string | null;
  orchestratorModelId: string | null;
  entries: Array<{ modelId: string; description?: string }>;
};

export function CustomModelEditor({
  value,
  scope,
  organizationId,
  options,
  availableScopes,
  onScopeChange,
  onSaved,
  onCancel,
}: {
  value: CustomModelRecord | null;
  scope: CustomModelScope;
  organizationId?: string;
  options: CustomModelOption[];
  availableScopes: CustomModelScope[];
  onScopeChange?: (scope: CustomModelScope) => void;
  onSaved: (model: CustomModelRecord) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(value?.name ?? "");
  const [description, setDescription] = useState(value?.description ?? "");
  const [type, setType] = useState<CustomModelType>(value?.type ?? "fallback");
  const [orchestratorModelId, setOrchestratorModelId] = useState(
    value?.orchestratorModelId ?? "",
  );
  const [entries, setEntries] = useState(() =>
    (value?.entries.length
      ? value.entries
      : [{ modelId: options[0]?.id ?? "", description: "" }]
    ).map((entry) => ({ ...entry, key: crypto.randomUUID() })),
  );
  const [draggedKey, setDraggedKey] = useState<string | null>(null);
  const [dropKey, setDropKey] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const editing = Boolean(value);
  const optionById = useMemo(
    () => new Map(options.map((option) => [option.id, option])),
    [options],
  );
  const pickerModels = useMemo<Model[]>(
    () =>
      options.map((option, index) => ({
        id: option.id,
        providerId: option.providerName,
        providerName: option.providerName,
        remoteId: option.providerName,
        name: option.name,
        description: null,
        icon: null,
        tags: [],
        enabled: true,
        sortOrder: index,
        toolsEnabled: false,
        capabilities: {
          vision: false,
          imageGeneration: false,
          tools: false,
        },
        canonicalSlug: null,
        contextLength: null,
        architecture: null,
        pricing: null,
        supportedParameters: [],
        providerMetadata: null,
        remoteMetadata: {},
      })),
    [options],
  );

  useEffect(() => {
    if (!entries.length && options[0])
      setEntries([
        { modelId: options[0].id, description: "", key: crypto.randomUUID() },
      ]);
  }, [entries.length, options]);

  const moveEntry = (key: string, targetIndex: number) => {
    const sourceIndex = entries.findIndex((entry) => entry.key === key);
    if (
      sourceIndex < 0 ||
      targetIndex < 0 ||
      targetIndex >= entries.length ||
      sourceIndex === targetIndex
    )
      return;
    const next = [...entries];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    setEntries(next);
    setAnnouncement(
      t("Model moved to position {{position}} of {{count}}", {
        position: targetIndex + 1,
        count: entries.length,
      }),
    );
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const body = {
        name,
        description,
        type,
        scope,
        organizationId: organizationId ?? null,
        orchestratorModelId:
          type === "routing" ? orchestratorModelId || null : null,
        entries: entries
          .filter((entry) => entry.modelId)
          .map(({ modelId, description }) => ({ modelId, description })),
      };
      const saved = await api<CustomModelRecord>(
        editing ? `/custom-models/${value!.id}` : "/custom-models",
        { method: editing ? "PATCH" : "POST", body: JSON.stringify(body) },
      );
      onSaved(saved);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Custom model could not be saved",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel p-5">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold">
            {editing ? "Edit custom model" : "New custom model"}
          </h2>
          <p className="mt-1 text-sm text-stone-500">
            {t(
              "Custom models appear everywhere a normal model can be selected.",
            )}
          </p>
        </div>
        <button type="button" className="button" onClick={onCancel}>
          {t("Cancel")}
        </button>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <label>
          <span className="label">{t("Name")}</span>
          <input
            className="field"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label>
          <span className="label">{t("Type")}</span>
          <select
            className="field"
            value={type}
            onChange={(event) => setType(event.target.value as CustomModelType)}
          >
            <option value="fallback">{t("Fallback")}</option>
            <option value="routing">{t("Custom routing")}</option>
          </select>
        </label>
        {availableScopes.length > 1 && (
          <label>
            <span className="label">{t("Resource scope")}</span>
            <select
              className="field"
              value={scope}
              onChange={(event) =>
                onScopeChange?.(event.target.value as CustomModelScope)
              }
            >
              {availableScopes.map((item) => (
                <option key={item} value={item}>
                  {item[0].toUpperCase() + item.slice(1)}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="md:col-span-2">
          <span className="label">{t("Description")}</span>
          <textarea
            className="field min-h-20"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
        {type === "routing" && (
          <label className="md:col-span-2">
            <span className="label">{t("Orchestrator")}</span>
            <ModelSearchPicker
              models={pickerModels}
              value={orchestratorModelId}
              emptyLabel="Choose an orchestrator"
              onChange={setOrchestratorModelId}
            />
          </label>
        )}
      </div>
      <div className="mt-5 rounded-xl border border-stone-200 p-4 dark:border-white/10">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold">{t("Models")}</h3>
            <p className="text-xs text-stone-500">
              {type === "routing"
                ? "The orchestrator chooses one model using these definitions."
                : t(
                    "Models are tried from top to bottom. Drag to reorder or use the arrow buttons.",
                  )}
            </p>
          </div>
          <button
            type="button"
            className="button"
            onClick={() =>
              setEntries((current) => [
                ...current,
                {
                  modelId: options[0]?.id ?? "",
                  description: "",
                  key: crypto.randomUUID(),
                },
              ])
            }
          >
            {t("Add model")}
          </button>
        </div>
        <p className="sr-only" role="status">
          {announcement}
        </p>
        <div className="space-y-3">
          {entries.map((entry, index) => (
            <div
              key={entry.key}
              data-fallback-entry={type === "fallback" ? index + 1 : undefined}
              onDragOver={(event) => {
                if (!draggedKey || type !== "fallback") return;
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
                setDropKey(entry.key);
              }}
              onDragLeave={(event) => {
                if (
                  !event.currentTarget.contains(
                    event.relatedTarget as Node | null,
                  )
                )
                  setDropKey(null);
              }}
              onDrop={(event) => {
                if (!draggedKey || type !== "fallback") return;
                event.preventDefault();
                moveEntry(draggedKey, index);
                setDraggedKey(null);
                setDropKey(null);
              }}
              className={`rounded-lg border p-2 ${type === "fallback" ? "flex items-start gap-2" : ""} ${dropKey === entry.key && draggedKey !== entry.key ? "border-lumen-600 ring-2 ring-lumen-600/30 dark:border-lumen-400 dark:ring-lumen-400/30" : "border-stone-200 dark:border-white/10"} ${draggedKey === entry.key ? "opacity-50" : ""}`}
            >
              {type === "fallback" && (
                <div className="grid shrink-0 grid-cols-2 items-center justify-items-center gap-1">
                  <span
                    className="text-xs font-semibold text-stone-500"
                    aria-hidden="true"
                  >
                    {index + 1}
                  </span>
                  <button
                    type="button"
                    className="button cursor-grab p-2 focus-visible:ring-2 focus-visible:ring-lumen-600 active:cursor-grabbing dark:focus-visible:ring-lumen-400"
                    draggable={entries.length > 1}
                    disabled={entries.length === 1}
                    aria-label={t("Drag model {{index}} to reorder", {
                      index: index + 1,
                    })}
                    title={t("Drag to reorder")}
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = "move";
                      event.dataTransfer.setData("text/plain", entry.key);
                      setDraggedKey(entry.key);
                    }}
                    onDragEnd={() => {
                      setDraggedKey(null);
                      setDropKey(null);
                    }}
                  >
                    <GripVertical size={16} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="button p-2 focus-visible:ring-2 focus-visible:ring-lumen-600 dark:focus-visible:ring-lumen-400"
                    disabled={index === 0}
                    aria-label={t("Move model {{index}} up", {
                      index: index + 1,
                    })}
                    title={t("Move up")}
                    onClick={() => moveEntry(entry.key, index - 1)}
                  >
                    <ArrowUp size={16} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="button p-2 focus-visible:ring-2 focus-visible:ring-lumen-600 dark:focus-visible:ring-lumen-400"
                    disabled={index === entries.length - 1}
                    aria-label={t("Move model {{index}} down", {
                      index: index + 1,
                    })}
                    title={t("Move down")}
                    onClick={() => moveEntry(entry.key, index + 1)}
                  >
                    <ArrowDown size={16} aria-hidden="true" />
                  </button>
                </div>
              )}
              <div className="grid min-w-0 flex-1 gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                <label>
                  <span className="sr-only">
                    {t("Model {{index}}", { index: index + 1 })}
                  </span>
                  <ModelSearchPicker
                    models={pickerModels}
                    value={entry.modelId}
                    onChange={(modelId) =>
                      setEntries((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, modelId } : item,
                        ),
                      )
                    }
                    emptyLabel="Choose a model"
                  />
                </label>
                <label>
                  <span className="sr-only">
                    {t("Definition for model {{index}}", { index: index + 1 })}
                  </span>
                  <input
                    className="field"
                    placeholder={
                      type === "routing"
                        ? "When should this model handle a prompt?"
                        : "Optional note"
                    }
                    value={entry.description ?? ""}
                    onChange={(event) =>
                      setEntries((current) =>
                        current.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, description: event.target.value }
                            : item,
                        ),
                      )
                    }
                  />
                </label>
                <button
                  type="button"
                  className="button"
                  aria-label={`Remove model ${index + 1}`}
                  disabled={entries.length === 1}
                  onClick={() =>
                    setEntries((current) =>
                      current.filter((_, itemIndex) => itemIndex !== index),
                    )
                  }
                >
                  {t("Remove")}
                </button>
                {entry.modelId && optionById.get(entry.modelId) && (
                  <span className="text-xs text-stone-400 md:col-span-2">
                    {optionById.get(entry.modelId)?.providerName}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" className="button" onClick={onCancel}>
          {t("Cancel")}
        </button>
        <button
          type="button"
          className="button-primary"
          disabled={
            busy ||
            !name.trim() ||
            !entries.some((entry) => entry.modelId) ||
            (type === "routing" && !orchestratorModelId)
          }
          onClick={() => void save()}
        >
          {busy ? "Saving…" : "Save custom model"}
        </button>
      </div>
    </section>
  );
}
